import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { useEffect, useState } from 'react';
import { DB_URL } from '@app/common';
import { getDataUrl } from '../../services/MediaFileService/MediaFileService';
import { useAppSelector } from 'src/store/hooks';

const ALLOWED_MEDIA_PREFIX = `${DB_URL.replace(/\/$/, '')}/`;
const isAllowedMediaUrl = (url?: string) => !!url && url.startsWith(ALLOWED_MEDIA_PREFIX);

const getMediaIdFromCouchDbUrl = (url: string | undefined, studyId: string | undefined) => {
    if (!url || !studyId || !isAllowedMediaUrl(url)) {
        return undefined;
    }

    try {
        const mediaUrl = new URL(url);
        const dbUrl = new URL(DB_URL);
        const dbPath = dbUrl.pathname.replace(/\/$/, '');
        const relativePath = mediaUrl.pathname.slice(dbPath.length).replace(/^\//, '');
        const [dbName, docId, attachmentName] = relativePath.split('/').map(decodeURIComponent);

        if (dbName !== `${studyId}-files` || !docId || !attachmentName) {
            return undefined;
        }

        return docId;
    } catch (e) {
        return undefined;
    }
};

const MarkdownImage = ({ src, alt }: { src?: string; alt?: string }) => {
    const studyId = useAppSelector(state => state.globalState.user?.studyId);
    const [imageSrc, setImageSrc] = useState('');

    useEffect(() => {
        const mediaId = getMediaIdFromCouchDbUrl(src, studyId);
        if (!mediaId) {
            setImageSrc('');
            return;
        }

        getDataUrl(mediaId, studyId).then(setImageSrc).catch(() => setImageSrc(''));
    }, [src, studyId]);

    return imageSrc ? <img src={imageSrc} alt={alt} /> : null;
};

const MarkdownVideo = ({ node, width, height, loop, muted }: any) => {
    const studyId = useAppSelector(state => state.globalState.user?.studyId);
    const [videoSrc, setVideoSrc] = useState('');
    const sourceSrc = node?.children?.find((child: any) => child.tagName === 'source')?.properties?.src;

    useEffect(() => {
        const mediaId = getMediaIdFromCouchDbUrl(sourceSrc, studyId);
        if (!mediaId) {
            setVideoSrc('');
            return;
        }

        getDataUrl(mediaId, studyId).then(setVideoSrc).catch(() => setVideoSrc(''));
    }, [sourceSrc, studyId]);

    return videoSrc ? (
        <video controls={true} width={width} height={height} loop={!!loop} muted={!!muted}>
            <source src={videoSrc} />
        </video>
    ) : null;
};

(DOMPurify as any).addHook('uponSanitizeAttribute', (node: any, data: any) => {
    if ((data.attrName === 'src' || data.attrName === 'poster') && (node.nodeName === 'VIDEO' || node.nodeName === 'SOURCE')) {
        if (!isAllowedMediaUrl(data.attrValue)) {
            data.keepAttr = false;
        }
    }
});

const MarkdownRenderer = ({ content }: { content: string }) => {
    const safeContent = DOMPurify.sanitize(content, {
        USE_PROFILES: { svg: true },
        ADD_TAGS: ['video', 'source'],
        ADD_ATTR: ['src', 'controls', 'poster', 'preload', 'loop', 'muted', 'playsinline', 'width', 'height'],
    });

    return (
        <ReactMarkdown
            remarkPlugins={[remarkBreaks]}
            rehypePlugins={[rehypeRaw]}
            components={{
                a: ({ node, ...props }) => <span>{props.children}</span>,
                img: ({ node, ...props }) => <MarkdownImage src={props.src} alt={props.alt} />,
                video: ({ node, ...props }) => <MarkdownVideo node={node} {...props} />,
            }}
        >
            {safeContent}
        </ReactMarkdown>
    )
}

export default MarkdownRenderer